import React from 'react';

// Modal Actions Component
interface ModalAction {
  label: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
}

interface ModalActionsProps {
  actions: ModalAction[];
  className?: string;
}

export const ModalActions: React.FC<ModalActionsProps> = ({ actions, className = '' }) => {
  const getButtonClasses = (variant: string, disabled: boolean) => {
    const baseClasses = 'inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    switch (variant) {
      case 'primary':
        return `${baseClasses} text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 hover:border-blue-700 focus:ring-blue-500 shadow-sm hover:shadow-md`;
      case 'secondary':
        return `${baseClasses} text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 hover:border-gray-400 focus:ring-gray-500`;
      case 'danger':
        return `${baseClasses} text-white bg-red-600 hover:bg-red-700 border border-red-600 hover:border-red-700 focus:ring-red-500 shadow-sm hover:shadow-md`;
      case 'success':
        return `${baseClasses} text-white bg-green-600 hover:bg-green-700 border border-green-600 hover:border-green-700 focus:ring-green-500 shadow-sm hover:shadow-md`;
      default:
        return `${baseClasses} text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 hover:border-gray-400 focus:ring-gray-500`;
    }
  };

  return (
    <div className={`flex justify-end space-x-3 pt-4 border-t border-gray-200 ${className}`}>
      {actions.map((action, index) => (
        <button
          key={index}
          type={action.type || 'button'}
          onClick={action.onClick}
          disabled={action.disabled || action.loading}
          className={getButtonClasses(action.variant || 'secondary', action.disabled || false)}
        >
          {action.loading && (
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {action.icon && !action.loading && (
            <span className="mr-2">{action.icon}</span>
          )}
          {action.label}
        </button>
      ))}
    </div>
  );
};

// Modal Form Field Component
interface ModalFormFieldProps {
  label: string;
  required?: boolean;
  helpText?: string;
  error?: string;
  children: React.ReactNode;
}

export const ModalFormField: React.FC<ModalFormFieldProps> = ({ 
  label, 
  required = false, 
  helpText, 
  error, 
  children 
}) => {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {helpText && (
        <p className="text-sm text-gray-500">{helpText}</p>
      )}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

// Searchable Dropdown Component
interface SearchableDropdownProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onFocus: () => void;
  placeholder: string;
  disabled?: boolean;
  loading?: boolean;
  items: any[];
  onSelect: (item: any) => void;
  selectedItem: any;
  onRemove: () => void;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  label,
  value,
  onChange,
  onFocus,
  placeholder,
  disabled = false,
  loading = false,
  items,
  onSelect,
  selectedItem,
  onRemove
}) => {
  const [showDropdown, setShowDropdown] = React.useState(false);

  const handleFocus = () => {
    setShowDropdown(true);
    onFocus();
  };

  const handleSelect = (item: any) => {
    onSelect(item);
    setShowDropdown(false);
  };

  return (
    <div className="space-y-2 relative">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
        />
        
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {/* Selected Item Display */}
      {selectedItem && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">{selectedItem.title}</p>
                <p className="text-xs text-green-600">{selectedItem.description || 'No description'}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onRemove}
              className="text-red-600 hover:text-red-800 transition-colors"
              title="Remove item"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Dropdown Items */}
      {showDropdown && items.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {items.map((item, index) => (
            <div
              key={item._id || index}
              onClick={() => handleSelect(item)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
            >
              <div className="font-medium text-gray-900">{item.title}</div>
              <div className="text-sm text-gray-500 truncate">{item.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
